/**
 * Geode Voice (UPT-2000 lot V0 §5.5) — section vocale du tiroir du copilote :
 * Adapté de JARVIS Local (licence MIT, © 2026 Thomas Berton) — voir THIRD_PARTY_NOTICE.md.
 * les transcriptions s'ajoutent au fil comme messages (utilisateur,
 * assistant) et les cartes sous la réponse. Lecture seule.
 */
import { useSyncExternalStore } from 'react';
import { useIntl } from '@/react-intl';
import { decideVoiceConfirmation, pendingVoiceConfirmationId, subscribeVoiceConfirm } from './voiceConfirm';
import { VoiceConfirmCard } from './VoiceConfirmCard';
import { VoiceMockInput } from './VoiceMockInput';
import { VoiceUsageLine } from './VoiceUsageLine';
import { useVoiceStore } from './voiceStore';

// Le tiroir du copilote affiche ses propres messages en texte simple
// (whitespace-pre-wrap) ; la section vocale suit la même convention — le
// rendu Markdown sûr (react-markdown) reste celui de VoicePanel, et
// react-markdown (ESM pur) casserait les tests jest 27 du tiroir.

export function VoiceDrawerSection() {
  const intl = useIntl();
  const t = (id: string) => intl.formatMessage({ id });
  const transcripts = useVoiceStore((store) => store.transcripts);
  const cards = useVoiceStore((store) => store.cards);
  const reports = useVoiceStore((store) => store.reports);
  const mockSessionRunning = useVoiceStore((store) => store.mockActive && store.state !== 'idle');
  // V1a : la ligne de consommation vit pendant et après une session RÉELLE de la page, jamais en simulateur.
  const showUsage = useVoiceStore((store) => store.realSession && !store.mockActive);
  // UPT-2124 J4 : une carte de confirmation (« Je clique sur Enregistrer ? ») se tranche ici aussi, par clic ou par la voix (J2d).
  const pendingConfirmId = useSyncExternalStore(subscribeVoiceConfirm, pendingVoiceConfirmationId, () => null);

  if (
    transcripts.length === 0 &&
    cards.length === 0 &&
    reports.length === 0 &&
    !mockSessionRunning &&
    !showUsage
  ) {
    return null;
  }

  return (
    <section aria-label={t('voice.panel.title')} data-voice-exclude="true" className="flex flex-col gap-2">
      {/* Ligne de consommation (V1a §5.2) en tête de la section. */}
      <VoiceUsageLine className="rounded-lg border border-portal-border px-3 py-2" />
      {transcripts.map((transcript) => (
        <div
          key={transcript.id}
          className={`rounded-lg px-3 py-2 text-sm ${
            transcript.role === 'user'
              ? 'self-end bg-portal-surface-muted text-portal-ink'
              : 'self-start bg-info-subtle text-info-text'
          }`}
        >
          <span className="me-1 text-xs font-semibold">
            {transcript.role === 'user' ? t('voice.panel.you') : t('voice.panel.assistant')}
          </span>
          {transcript.text}
        </div>
      ))}
      {cards.map((card) =>
        card.kind === 'confirm' ? (
          <VoiceConfirmCard
            key={card.id}
            card={card}
            onConfirm={
              card.confirm?.status === 'pending' && card.confirm.confirmationId === pendingConfirmId
                ? () => void decideVoiceConfirmation('yes', card.confirm?.confirmationId)
                : undefined
            }
            onCancel={
              card.confirm?.status === 'pending' && card.confirm.confirmationId === pendingConfirmId
                ? () => void decideVoiceConfirmation('no', card.confirm?.confirmationId)
                : undefined
            }
          />
        ) : (
          <section key={card.id} className="rounded-lg border border-portal-border px-3 py-2">
            <h3 className="mb-1 text-sm font-semibold text-portal-ink">{card.title}</h3>
            <div className="whitespace-pre-wrap text-sm text-portal-ink">{card.bodyMd}</div>
          </section>
        )
      )}
      {reports.map((report) => (
        <section key={report.id} className="rounded-lg border border-portal-border px-3 py-2">
          <h3 className="mb-1 text-sm font-semibold text-portal-ink">{report.title}</h3>
          {report.kpis && report.kpis.length > 0 ? (
            <ul className="mb-2 flex flex-wrap gap-2">
              {report.kpis.map((kpi, index) => (
                <li key={index} className="rounded bg-portal-surface-muted px-2 py-1 text-xs">
                  <span className="text-portal-ink-muted">{kpi.label}</span>{' '}
                  <span className="font-semibold text-portal-ink">{kpi.value}</span>
                </li>
              ))}
            </ul>
          ) : null}
          {report.table ? (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-xs">
                <thead>
                  <tr>
                    {report.table.columns.map((column, index) => (
                      <th key={index} className="border-b border-portal-border px-2 py-1 text-start font-medium text-portal-ink-muted">
                        {column}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {report.table.rows.map((row, rowIndex) => (
                    <tr key={rowIndex}>
                      {row.map((cell, cellIndex) => (
                        <td key={cellIndex} className="border-b border-portal-border px-2 py-1 text-portal-ink">
                          {cell}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </section>
      ))}
      {/* Simulateur : la saisie remplace le micro, ici aussi (revue CORE 11.10). */}
      <VoiceMockInput />
    </section>
  );
}

export default VoiceDrawerSection;
